import React, { useState, useEffect, useRef } from "react";
import {
  Brain,
  Target,
  BarChart3,
  RefreshCw,
  Eye,
  EyeOff,
  Play,
} from "lucide-react";

const MemoryChallengeGame = () => {
  // 游戏状态
  const [currentMode, setCurrentMode] = useState(null);
  const [gamePhase, setGamePhase] = useState("setup");
  const [timeLeft, setTimeLeft] = useState(0);
  const [statistics, setStatistics] = useState({
    mode1: { attempts: 0, totalCorrect: 0, bestScore: 0 },
    mode2: { attempts: 0, expressionAccuracy: 0, wordAccuracy: 0 },
    mode3: { attempts: 0, totalCorrect: 0, bestScore: 0 },
  });

  // 模式1状态
  const [mode1Config, setMode1Config] = useState({ digits: 6, displayTime: 5 });
  const [generatedNumber, setGeneratedNumber] = useState("");
  const [userNumberInput, setUserNumberInput] = useState("");
  const [mode1Result, setMode1Result] = useState("");

  // 模式2状态
  const [mode2Config, setMode2Config] = useState({ displayTime: 10 });
  const [expressions, setExpressions] = useState([]);
  const [words, setWords] = useState([]);
  const [userExpressionChecks, setUserExpressionChecks] = useState([]);
  const [userWordInputs, setUserWordInputs] = useState(["", "", "", ""]);
  const [mode2Result, setMode2Result] = useState("");

  // 模式3状态
  const [mode3Config, setMode3Config] = useState({
    digits: 5,
    displayTime: 8,
    interferenceLevel: 2,
  });
  const [activeInterferences, setActiveInterferences] = useState([]);
  const [mode3Result, setMode3Result] = useState("");

  const [isMasked, setIsMasked] = useState(false);
  const timerRef = useRef(null);

  // 词语库
  const WORDS_POOL = [
    "电灯",
    "书本",
    "山峰",
    "喜鹊",
    "衣柜",
    "城市",
    "图像",
    "帮助",
    "现代",
    "黑板",
    "杯子",
    "晴天",
    "遥控",
    "纸巾",
    "电荷",
    "溶液",
    "忙碌",
    "桌面",
    "火车",
    "木马",
    "白云",
    "年轮",
    "乐器",
    "书签",
    "菠萝",
    "文件",
    "太阳",
    "手机",
    "沙漠",
    "音乐",
    "跑道",
    "灯笼",
    "火焰",
    "相片",
    "海洋",
    "石油",
    "墙壁",
    "键盘",
    "窗户",
    "花朵",
  ];

  const INTERFERENCE_COLORS = [
    "红色",
    "蓝色",
    "绿色",
    "黄色",
    "紫色",
    "橙色",
    "粉色",
    "灰色",
  ];
  const INTERFERENCE_NUMBERS = [
    "一",
    "二",
    "三",
    "四",
    "五",
    "六",
    "七",
    "八",
    "九",
  ];

  // 计时器逻辑
  useEffect(() => {
    if (gamePhase === "displaying" && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (gamePhase === "displaying" && timeLeft === 0) {
      setIsMasked(true);
      setGamePhase("answering");
    }
    return () => clearTimeout(timerRef.current);
  }, [gamePhase, timeLeft]);

  // 模式1：随机数生成
  const generateRandomNumber = () => {
    const number = Array.from({ length: mode1Config.digits }, () =>
      Math.floor(Math.random() * 10)
    ).join("");
    setGeneratedNumber(number);
    setTimeLeft(mode1Config.displayTime);
    setGamePhase("displaying");
    setIsMasked(false);
    setUserNumberInput("");
    setMode1Result("");
  };

  // 模式1：验证结果
  const checkMode1Result = () => {
    if (!generatedNumber) return;

    let correctCount = 0;
    for (let i = 0; i < generatedNumber.length; i++) {
      if (userNumberInput[i] === generatedNumber[i]) {
        correctCount++;
      }
    }

    const accuracy = ((correctCount / generatedNumber.length) * 100).toFixed(1);
    setMode1Result({
      correctCount,
      total: generatedNumber.length,
      accuracy: parseFloat(accuracy),
      userInput: userNumberInput,
      correct: generatedNumber,
    });

    setStatistics((prev) => ({
      ...prev,
      mode1: {
        attempts: prev.mode1.attempts + 1,
        totalCorrect: prev.mode1.totalCorrect + correctCount,
        bestScore: Math.max(prev.mode1.bestScore, parseFloat(accuracy)),
      },
    }));

    setGamePhase("result");
  };

  // 模式2：生成表达式和词语
  const generateMode2Content = () => {
    const newExpressions = Array.from({ length: 4 }, () => {
      let a = Math.floor(Math.random() * 16) + 5;
      let b = Math.floor(Math.random() * 16) + 5;

      // 避免两个都是个位数
      if (a < 10 && b < 10) {
        if (Math.random() < 0.5) {
          a = Math.floor(Math.random() * 10) + 10;
        } else {
          b = Math.floor(Math.random() * 10) + 10;
        }
      }

      // 避免出现10
      if (a === 10) a = 11;
      if (b === 10) b = 11;

      const correct = a * b;
      const isCorrect = Math.random() < 0.5;

      let displayResult = correct;
      if (!isCorrect) {
        if (correct >= 10 && Math.random() < 0.5) {
          let tens = Math.floor(correct / 10);
          let ones = correct % 10;
          if (Math.random() < 0.5) {
            tens = tens + 1;
          } else {
            tens = tens - 1;
          }
          if (tens < 0) tens = 0;
          displayResult = tens * 10 + ones;
        } else {
          const delta = Math.floor(Math.random() * 5) + 1;
          if (Math.random() < 0.5) {
            displayResult = correct + delta;
          } else {
            displayResult = correct - delta;
            if (displayResult < 0) displayResult = 0;
          }
        }
      }

      return {
        expression: `${a} × ${b} = ${displayResult}`,
        isCorrect,
        a,
        b,
        correct,
        displayResult,
      };
    });

    const shuffled = [...WORDS_POOL].sort(() => Math.random() - 0.5);
    const newWords = shuffled.slice(0, 4);

    setExpressions(newExpressions);
    setWords(newWords);
    setUserExpressionChecks(new Array(4).fill(false));
    setUserWordInputs(["", "", "", ""]);
    setTimeLeft(mode2Config.displayTime);
    setGamePhase("displaying");
    setIsMasked(false);
    setMode2Result("");
  };

  // 模式2：验证结果
  const checkMode2Result = () => {
    let expressionCorrect = 0;
    let wordCorrect = 0;

    expressions.forEach((expr, idx) => {
      if (expr.isCorrect === userExpressionChecks[idx]) {
        expressionCorrect++;
      }
    });

    words.forEach((word, idx) => {
      if (word === userWordInputs[idx].trim()) {
        wordCorrect++;
      }
    });

    const exprAccuracy = ((expressionCorrect / 4) * 100).toFixed(1);
    const wordAccuracy = ((wordCorrect / 4) * 100).toFixed(1);

    setMode2Result({
      expressionCorrect,
      wordCorrect,
      expressionAccuracy: parseFloat(exprAccuracy),
      wordAccuracy: parseFloat(wordAccuracy),
      expressions,
      words,
      userChecks: userExpressionChecks,
      userWords: userWordInputs,
    });

    setStatistics((prev) => ({
      ...prev,
      mode2: {
        attempts: prev.mode2.attempts + 1,
        expressionAccuracy:
          (prev.mode2.expressionAccuracy * prev.mode2.attempts +
            parseFloat(exprAccuracy)) /
          (prev.mode2.attempts + 1),
        wordAccuracy:
          (prev.mode2.wordAccuracy * prev.mode2.attempts +
            parseFloat(wordAccuracy)) /
          (prev.mode2.attempts + 1),
      },
    }));

    setGamePhase("result");
  };

  // 模式3：干扰任务模式
  const generateMode3Content = () => {
    const number = Array.from({ length: mode3Config.digits }, () =>
      Math.floor(Math.random() * 10)
    ).join("");
    setGeneratedNumber(number);

    const interferences = [];
    for (let i = 0; i < mode3Config.interferenceLevel * 4; i++) {
      interferences.push({
        id: i,
        type: Math.random() < 0.5 ? "color" : "number",
        content:
          Math.random() < 0.5
            ? INTERFERENCE_COLORS[
                Math.floor(Math.random() * INTERFERENCE_COLORS.length)
              ]
            : INTERFERENCE_NUMBERS[
                Math.floor(Math.random() * INTERFERENCE_NUMBERS.length)
              ],
        delay: Math.random() * mode3Config.displayTime * 1000,
        x: Math.random() * 80 + 10,
        y: Math.random() * 80 + 10,
        duration: 1500 + Math.random() * 1000,
      });
    }

    setActiveInterferences([]);
    setTimeLeft(mode3Config.displayTime);
    setGamePhase("displaying");
    setIsMasked(false);
    setUserNumberInput("");
    setMode3Result("");

    interferences.forEach((item) => {
      setTimeout(() => {
        setActiveInterferences((prev) => [...prev, item]);
        setTimeout(() => {
          setActiveInterferences((prev) =>
            prev.filter((i) => i.id !== item.id)
          );
        }, item.duration);
      }, item.delay);
    });
  };

  const checkMode3Result = () => {
    if (!generatedNumber) return;

    let correctCount = 0;
    for (let i = 0; i < generatedNumber.length; i++) {
      if (userNumberInput[i] === generatedNumber[i]) {
        correctCount++;
      }
    }

    const accuracy = ((correctCount / generatedNumber.length) * 100).toFixed(1);
    setMode3Result({
      correctCount,
      total: generatedNumber.length,
      accuracy: parseFloat(accuracy),
      userInput: userNumberInput,
      correct: generatedNumber,
      interferenceLevel: mode3Config.interferenceLevel,
    });

    setStatistics((prev) => ({
      ...prev,
      mode3: {
        attempts: prev.mode3.attempts + 1,
        totalCorrect: prev.mode3.totalCorrect + correctCount,
        bestScore: Math.max(prev.mode3.bestScore, parseFloat(accuracy)),
      },
    }));

    setGamePhase("result");
  };

  const resetGame = () => {
    setGamePhase("setup");
    setCurrentMode(null);
    setIsMasked(false);
    setTimeLeft(0);
    setActiveInterferences([]);
    clearTimeout(timerRef.current);
  };

  const renderModeSelector = () => (
    <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4 flex items-center justify-center gap-3">
          <Brain className="text-blue-500" />
          工作记忆挑战实验室
        </h1>
        <p className="text-gray-600 text-lg">
          探索你的大脑认知极限，理解工作记忆的神奇与局限
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div
          onClick={() => setCurrentMode(1)}
          className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-300"
        >
          <div className="flex items-center gap-3 mb-3">
            <Target className="text-blue-600" size={24} />
            <h3 className="text-xl font-semibold text-blue-800">
              模式1：纯记忆挑战
            </h3>
          </div>
          <p className="text-blue-700">测试短时记忆容量，体验7±2法则</p>
        </div>

        <div
          onClick={() => setCurrentMode(2)}
          className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-purple-300"
        >
          <div className="flex items-center gap-3 mb-3">
            <RefreshCw className="text-purple-600" size={24} />
            <h3 className="text-xl font-semibold text-purple-800">
              模式2：双任务挑战
            </h3>
          </div>
          <p className="text-purple-700">同时处理记忆与推理，感受认知负荷</p>
        </div>

        <div
          onClick={() => setCurrentMode(3)}
          className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-lg cursor-pointer hover:shadow-lg transition-all border-2 border-transparent hover:border-red-300"
        >
          <div className="flex items-center gap-3 mb-3">
            <Eye className="text-red-600" size={24} />
            <h3 className="text-xl font-semibold text-red-800">
              模式3：抗干扰挑战
            </h3>
          </div>
          <p className="text-red-700">在干扰环境中保持专注，测试注意力控制</p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="text-gray-600" size={20} />
          你的表现统计
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {statistics.mode1.bestScore.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">纯记忆最佳准确率</div>
            <div className="text-xs text-gray-500">
              {statistics.mode1.attempts} 次尝试
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {statistics.mode2.attempts > 0
                ? `${(
                    (statistics.mode2.expressionAccuracy +
                      statistics.mode2.wordAccuracy) /
                    2
                  ).toFixed(1)}%`
                : "0%"}
            </div>
            <div className="text-sm text-gray-600">双任务平均表现</div>
            <div className="text-xs text-gray-500">
              {statistics.mode2.attempts} 次尝试
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {statistics.mode3.bestScore.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">抗干扰最佳表现</div>
            <div className="text-xs text-gray-500">
              {statistics.mode3.attempts} 次尝试
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMode1 = () => (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-2">
          <Target className="text-blue-600" size={24} />
          纯记忆挑战
        </h2>
        <button
          onClick={resetGame}
          className="text-gray-500 hover:text-gray-700"
        >
          返回
        </button>
      </div>

      {gamePhase === "setup" && (
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-blue-800 mb-2">
              <strong>挑战说明：</strong>
            </p>
            <p className="text-blue-700">
              记住显示的随机数字序列。研究表明，大多数人的短时记忆容量约为7±2个项目。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                数字位数
              </label>
              <input
                type="range"
                min="3"
                max="13"
                value={mode1Config.digits}
                onChange={(e) =>
                  setMode1Config({
                    ...mode1Config,
                    digits: parseInt(e.target.value),
                  })
                }
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600 mt-1">
                {mode1Config.digits} 位数字
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                显示时间
              </label>
              <input
                type="range"
                min="2"
                max="10"
                value={mode1Config.displayTime}
                onChange={(e) =>
                  setMode1Config({
                    ...mode1Config,
                    displayTime: parseInt(e.target.value),
                  })
                }
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600 mt-1">
                {mode1Config.displayTime} 秒
              </div>
            </div>
          </div>

          <button
            onClick={generateRandomNumber}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <Play size={20} />
            开始挑战
          </button>
        </div>
      )}

      {gamePhase === "displaying" && (
        <div className="text-center">
          <div className="mb-4">
            <div className="text-lg text-gray-600 mb-2">请记住这些数字</div>
            <div className="bg-blue-100 rounded-lg p-2 mb-4">
              <div className="text-sm text-blue-600">
                剩余时间: {timeLeft} 秒
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 mt-1">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
                  style={{
                    width: `${(timeLeft / mode1Config.displayTime) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="text-6xl font-mono font-bold text-blue-800 tracking-wider mb-4 select-none">
            {generatedNumber}
          </div>
        </div>
      )}

      {gamePhase === "answering" && (
        <div className="text-center space-y-6">
          <div className="bg-gray-100 rounded-lg p-8">
            {isMasked ? (
              <div className="text-gray-500">
                <EyeOff size={48} className="mx-auto mb-2" />
                <p>数字已隐藏，请输入你记住的数字</p>
              </div>
            ) : (
              <div className="text-6xl font-mono font-bold text-blue-800 tracking-wider">
                {generatedNumber}
              </div>
            )}
          </div>

          <div>
            <input
              type="text"
              value={userNumberInput}
              onChange={(e) => setUserNumberInput(e.target.value)}
              placeholder="输入你记住的数字"
              className="text-center text-2xl font-mono border-2 border-blue-300 rounded-lg p-3 w-64"
              maxLength={mode1Config.digits}
            />
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={checkMode1Result}
              className="bg-green-600 text-white py-2 px-6 rounded-lg hover:bg-green-700 transition-colors"
            >
              验证答案
            </button>
            <button
              onClick={() => setIsMasked(!isMasked)}
              className="bg-gray-600 text-white py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              {isMasked ? <Eye size={16} /> : <EyeOff size={16} />}
              {isMasked ? "显示" : "隐藏"}
            </button>
          </div>
        </div>
      )}

      {gamePhase === "result" && mode1Result && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="text-4xl font-bold mb-2">
              {mode1Result.accuracy >= 80
                ? "🎉"
                : mode1Result.accuracy >= 60
                ? "👍"
                : "💪"}
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {mode1Result.accuracy}% 准确率
            </div>
            <div className="text-gray-600">
              正确 {mode1Result.correctCount} 位，共 {mode1Result.total} 位
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">正确答案</div>
              <div className="text-2xl font-mono font-bold text-green-600 mb-3">
                {mode1Result.correct}
              </div>

              <div className="text-sm text-gray-600 mb-2">你的输入</div>
              <div className="text-2xl font-mono font-bold">
                {mode1Result.userInput.split("").map((digit, idx) => (
                  <span
                    key={idx}
                    className={
                      digit === mode1Result.correct[idx]
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {digit || "_"}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              <strong>认知科学小知识：</strong>
              {mode1Result.accuracy >= 80
                ? "优秀！你的短时记忆容量超过了大多数人。"
                : mode1Result.accuracy >= 60
                ? "不错！这接近大多数人的短时记忆极限（7±2项目）。"
                : "这很正常！超过记忆容量时，准确率会显著下降。尝试减少位数或使用记忆策略。"}
            </p>
          </div>

          <button
            onClick={() => setGamePhase("setup")}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
          >
            再次挑战
          </button>
        </div>
      )}
    </div>
  );

  const renderMode2 = () => (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-purple-800 flex items-center gap-2">
          <RefreshCw className="text-purple-600" size={24} />
          双任务挑战
        </h2>
        <button
          onClick={resetGame}
          className="text-gray-500 hover:text-gray-700"
        >
          返回
        </button>
      </div>

      {gamePhase === "setup" && (
        <div className="space-y-6">
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-purple-800 mb-2">
              <strong>挑战说明：</strong>
            </p>
            <p className="text-purple-700">
              同时完成两个任务：判断算式正确性 +
              记忆词语。这将考验你的工作记忆和注意力分配能力。
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              显示时间
            </label>
            <input
              type="range"
              min="5"
              max="15"
              value={mode2Config.displayTime}
              onChange={(e) =>
                setMode2Config({ displayTime: parseInt(e.target.value) })
              }
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600 mt-1">
              {mode2Config.displayTime} 秒
            </div>
          </div>

          <button
            onClick={generateMode2Content}
            className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
          >
            <Play size={20} />
            开始挑战
          </button>
        </div>
      )}

      {gamePhase === "displaying" && (
        <div className="text-center">
          <div className="mb-4">
            <div className="text-lg text-gray-600 mb-2">
              请同时记住算式的正确性和所有词语
            </div>
            <div className="bg-purple-100 rounded-lg p-2 mb-4">
              <div className="text-sm text-purple-600">
                剩余时间: {timeLeft} 秒
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2 mt-1">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-1000"
                  style={{
                    width: `${(timeLeft / mode2Config.displayTime) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                词语
              </h3>
              <div className="space-y-3">
                {words.map((word, idx) => (
                  <div
                    key={idx}
                    className="bg-purple-50 p-3 rounded-lg text-lg font-semibold"
                  >
                    {word}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                算式
              </h3>
              <div className="space-y-3">
                {expressions.map((expr, idx) => (
                  <div
                    key={idx}
                    className="bg-purple-50 p-3 rounded-lg text-lg font-mono"
                  >
                    {expr.expression}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {gamePhase === "answering" && (
        <div className="space-y-8">
          <div className="bg-gray-100 rounded-lg p-6 text-center text-gray-500">
            内容已隐藏，请根据记忆作答
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-green-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-green-800 flex items-center gap-2">
                🧠 填写记住的词语
              </h3>
              <div className="space-y-3">
                {userWordInputs.map((word, idx) => (
                  <div key={idx}>
                    <label className="block text-sm font-medium text-green-700 mb-1">
                      词语 {idx + 1}
                    </label>
                    <input
                      type="text"
                      value={word}
                      onChange={(e) => {
                        const newInputs = [...userWordInputs];
                        newInputs[idx] = e.target.value;
                        setUserWordInputs(newInputs);
                      }}
                      className="w-full border-2 border-green-200 rounded-lg px-3 py-2 focus:border-green-400 focus:outline-none"
                      placeholder={`输入第${idx + 1}个词语`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-blue-800 flex items-center gap-2">
                🎯 判断算式正确性（勾选正确的）
              </h3>
              <div className="space-y-3">
                {expressions.map((expr, idx) => (
                  <label
                    key={idx}
                    className="flex items-center space-x-3 p-3 hover:bg-blue-100 rounded-lg cursor-pointer border border-blue-200"
                  >
                    <input
                      type="checkbox"
                      checked={userExpressionChecks[idx] || false}
                      onChange={(e) => {
                        const newChecks = [...userExpressionChecks];
                        newChecks[idx] = e.target.checked;
                        setUserExpressionChecks(newChecks);
                      }}
                      className="w-5 h-5 text-blue-600"
                    />
                    <span className="font-medium">算式 {idx + 1}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={checkMode2Result}
              className="bg-green-600 text-white py-3 px-8 rounded-lg hover:bg-green-700 transition-colors text-lg font-semibold"
            >
              提交答案
            </button>
          </div>
        </div>
      )}

      {gamePhase === "result" && mode2Result && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="text-4xl font-bold mb-2">
              {(mode2Result.expressionAccuracy + mode2Result.wordAccuracy) /
                2 >=
              75
                ? "🎉"
                : (mode2Result.expressionAccuracy + mode2Result.wordAccuracy) /
                    2 >=
                  50
                ? "👍"
                : "💪"}
            </div>
            <div className="text-2xl font-bold text-purple-600">
              总体表现:{" "}
              {(
                (mode2Result.expressionAccuracy + mode2Result.wordAccuracy) /
                2
              ).toFixed(1)}
              %
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-blue-600">
                  {mode2Result.expressionAccuracy}%
                </div>
                <div className="text-sm text-gray-600">算式判断准确率</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600">
                  {mode2Result.wordAccuracy}%
                </div>
                <div className="text-sm text-gray-600">词语记忆准确率</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-3">
                词语记忆详情
              </h4>
              {mode2Result.words.map((word, idx) => (
                <div key={idx} className="mb-2 text-sm">
                  <span className="font-semibold">{word}</span>
                  <span className="mx-2">→</span>
                  <span
                    className={`${
                      word === mode2Result.userWords[idx].trim()
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {mode2Result.userWords[idx] || "(未填写)"}
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-3">算式结果详情</h4>
              {mode2Result.expressions.map((expr, idx) => (
                <div
                  key={idx}
                  className="mb-2 text-sm flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <span className="font-mono">{expr.expression}</span>
                    <span className="ml-2 text-xs text-gray-500">
                      (实际{expr.isCorrect ? "正确" : "错误"})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600">
                      你{mode2Result.userChecks[idx] ? "勾选了" : "未勾选"}
                    </span>
                    <span
                      className={`text-lg ${
                        expr.isCorrect === mode2Result.userChecks[idx]
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {expr.isCorrect === mode2Result.userChecks[idx]
                        ? "✓"
                        : "✗"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-purple-800 text-sm">
              <strong>认知科学发现：</strong>
              {(mode2Result.expressionAccuracy + mode2Result.wordAccuracy) /
                2 >=
              75
                ? "出色！你能很好地分配认知资源处理多个任务。"
                : (mode2Result.expressionAccuracy + mode2Result.wordAccuracy) /
                    2 >=
                  50
                ? "当工作记忆需要处理多个任务时，每个任务的表现通常会下降。这体现了认知资源的有限性。"
                : "双任务干扰很明显！这说明工作记忆容量是有限的，多任务会导致表现显著下降。"}
            </p>
          </div>

          <button
            onClick={() => setGamePhase("setup")}
            className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors"
          >
            再次挑战
          </button>
        </div>
      )}
    </div>
  );

  const renderMode3 = () => (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-red-800 flex items-center gap-2">
          <Eye className="text-red-600" size={24} />
          抗干扰挑战
        </h2>
        <button
          onClick={resetGame}
          className="text-gray-500 hover:text-gray-700"
        >
          返回
        </button>
      </div>

      {gamePhase === "setup" && (
        <div className="space-y-6">
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-red-800 mb-2">
              <strong>挑战说明：</strong>
            </p>
            <p className="text-red-700">
              在记忆数字的同时，屏幕上会出现干扰信息。这考验你的选择性注意力和抗干扰能力。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                数字位数
              </label>
              <input
                type="range"
                min="3"
                max="12"
                value={mode3Config.digits}
                onChange={(e) =>
                  setMode3Config({
                    ...mode3Config,
                    digits: parseInt(e.target.value),
                  })
                }
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600 mt-1">
                {mode3Config.digits} 位
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                显示时间
              </label>
              <input
                type="range"
                min="5"
                max="15"
                value={mode3Config.displayTime}
                onChange={(e) =>
                  setMode3Config({
                    ...mode3Config,
                    displayTime: parseInt(e.target.value),
                  })
                }
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600 mt-1">
                {mode3Config.displayTime} 秒
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                干扰强度
              </label>
              <input
                type="range"
                min="1"
                max="5"
                value={mode3Config.interferenceLevel}
                onChange={(e) =>
                  setMode3Config({
                    ...mode3Config,
                    interferenceLevel: parseInt(e.target.value),
                  })
                }
                className="w-full"
              />
              <div className="text-center text-sm text-gray-600 mt-1">
                级别 {mode3Config.interferenceLevel}
              </div>
            </div>
          </div>

          <button
            onClick={generateMode3Content}
            className="w-full bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
          >
            <Play size={20} />
            开始挑战
          </button>
        </div>
      )}

      {gamePhase === "displaying" && (
        <div className="text-center relative">
          <div className="mb-4">
            <div className="text-lg text-gray-600 mb-2">
              专注记忆数字，忽略干扰信息
            </div>
            <div className="bg-red-100 rounded-lg p-2 mb-4">
              <div className="text-sm text-red-600">
                剩余时间: {timeLeft} 秒
              </div>
              <div className="w-full bg-red-200 rounded-full h-2 mt-1">
                <div
                  className="bg-red-600 h-2 rounded-full transition-all duration-1000"
                  style={{
                    width: `${(timeLeft / mode3Config.displayTime) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="text-6xl font-mono font-bold text-red-800 tracking-wider mb-4 select-none relative z-10">
            {generatedNumber}
          </div>

          <div className="text-sm text-red-600">
            ⚠️ 注意：屏幕上会出现干扰文字，请专注于记忆数字
          </div>

          {activeInterferences.map((item) => (
            <div
              key={item.id}
              className="absolute text-2xl font-bold z-20 animate-pulse"
              style={{
                left: `${item.x}%`,
                top: `${item.y}%`,
                color: item.type === "color" ? "#ff4444" : "#4444ff",
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              {item.content}
            </div>
          ))}
        </div>
      )}

      {gamePhase === "answering" && (
        <div className="text-center space-y-6">
          <div className="bg-gray-100 rounded-lg p-8">
            <div className="text-gray-500">
              <div className="text-5xl mb-2">🙈</div>
              <p>数字已隐藏，请输入你记住的数字</p>
            </div>
          </div>

          <div>
            <input
              type="text"
              value={userNumberInput}
              onChange={(e) => setUserNumberInput(e.target.value)}
              placeholder="输入你记住的数字"
              className="text-center text-2xl font-mono border-2 border-red-300 rounded-lg p-3 w-64"
              maxLength={mode3Config.digits}
            />
          </div>

          <button
            onClick={checkMode3Result}
            className="bg-green-600 text-white py-2 px-6 rounded-lg hover:bg-green-700 transition-colors"
          >
            验证答案
          </button>
        </div>
      )}

      {gamePhase === "result" && mode3Result && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="text-4xl font-bold mb-2">
              {mode3Result.accuracy >= 80
                ? "🎯"
                : mode3Result.accuracy >= 60
                ? "💪"
                : "🧠"}
            </div>
            <div className="text-2xl font-bold text-red-600">
              {mode3Result.accuracy}% 准确率
            </div>
            <div className="text-gray-600">
              干扰级别 {mode3Result.interferenceLevel} 下，正确{" "}
              {mode3Result.correctCount} 位，共 {mode3Result.total} 位
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">正确答案</div>
              <div className="text-2xl font-mono font-bold text-green-600 mb-3">
                {mode3Result.correct}
              </div>

              <div className="text-sm text-gray-600 mb-2">你的输入</div>
              <div className="text-2xl font-mono font-bold">
                {mode3Result.userInput.split("").map((digit, idx) => (
                  <span
                    key={idx}
                    className={
                      digit === mode3Result.correct[idx]
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {digit || "_"}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-red-800 text-sm">
              <strong>注意力科学：</strong>
              {mode3Result.accuracy >= 80
                ? "优秀的选择性注意力！你能很好地过滤干扰信息。"
                : mode3Result.accuracy >= 60
                ? "在干扰环境下保持专注是很有挑战性的，这是正常的表现。"
                : "干扰显著影响了记忆表现。这说明我们的注意力资源是有限的，需要策略性地分配。"}
            </p>
          </div>

          <button
            onClick={() => setGamePhase("setup")}
            className="w-full bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition-colors"
          >
            再次挑战
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="max-w-4xl mx-auto">
        {!currentMode && renderModeSelector()}
        {currentMode === 1 && renderMode1()}
        {currentMode === 2 && renderMode2()}
        {currentMode === 3 && renderMode3()}
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default MemoryChallengeGame;
